import { api, setCsrfToken } from "./client";
export { connectorApi } from "./connectors";
export { dmCampaignApi, dmEngagementApi, dmSuppressionApi } from "./dm";
export { advertisersApi, advertiserPortalApi } from "./advertisers";
export { ocrApi, ocrUploadApi } from "./ocr";
import type {
  AuthMe,
  PaginatedResponse,
  FaxTransmission,
  FaxDetail,
  DashboardStats,
  ProductAlias,
  ImportCodeMap,
  ProductMapping,
  DictionarySuggestion,
  DictionarySuggestionDecisionStatus,
  DictionarySuggestionRejectReason,
  PlatformTenant,
  PlatformTenantDetail,
  PlatformIntakeRecord,
  DemoTenantSummary,
  TrialApplicationItem,
  TrialFaxNumberInventoryItem,
  TrialNotificationDeliveryItem,
  TrialDataRetentionItem,
  TrialConversionItem,
  TrialConversionPreparationPreview,
  TrialConversionPreflight,
  AllowedFaxRecipient,
  RuntimeControls,
  UploadOcrStatus,
  FaxSchedulerHealth,
  UsageSummary,
  BillingPlan,
  Invoice,
  InvoiceDetail,
  BillingSummaryItem,
  PointBalance,
  PointTransaction,
  ApiKey,
  ApiKeyManagementPolicy,
  ApiKeyCreateResponse,
  ApiKeyScope,
  OnboardingStatus,
  OnboardingCompleteResponse,
  AiAttributionStats,
  DmSuppressionsResponse,
} from "@/types";

// Auth
type AuthLoginSuccess = { ok: true; csrfToken?: string; user: AuthMe["user"]; tenant: AuthMe["tenant"] };
type AuthMfaRequired = { ok: false; mfaRequired: true; challengeToken: string };
type AuthPasswordChangeRequired = {
  ok: false;
  passwordChangeRequired: true;
  challengeToken: string;
  policy: { minLength: number; requiredCharacterClasses: number; maxAgeDays: number };
};
export type AuthLoginResult = AuthLoginSuccess | AuthMfaRequired | AuthPasswordChangeRequired;

export const authApi = {
  me: async () => {
    const data = await api.get<AuthMe & { csrfToken?: string }>("/auth/me");
    if (data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  logout: async () => {
    const data = await api.post<{ ok: boolean }>("/auth/logout", {});
    setCsrfToken(null);
    return data;
  },
  googleLogin: async (credential: string) => {
    const data = await api.post<AuthLoginResult>("/auth/google", { credential });
    if (data.ok && data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  getGoogleClientId: () =>
    api.get<{ clientId: string }>("/auth/google-client-id"),
  microsoftStatus: () =>
    api.get<{ configured: boolean }>("/auth/microsoft/status"),
  startMicrosoftLogin: () =>
    api.post<{ authorizationUrl: string }>("/auth/microsoft/start", {}),
  startEnterpriseMicrosoftLogin: (email: string) =>
    api.post<{ authorizationUrl: string }>("/auth/microsoft/enterprise/start", { email }),
  getMicrosoftLinkStatus: () =>
    api.get<{ configured: boolean; linked: boolean }>("/auth/microsoft/link/status"),
  startMicrosoftLink: () =>
    api.post<{ authorizationUrl: string }>("/auth/microsoft/link/start", {}),
  pendingMicrosoftMfa: () =>
    api.get<{ challengeToken: string }>("/auth/microsoft/pending-mfa"),
  enterpriseSsoStatus: () =>
    api.get<{ configured: boolean }>("/auth/sso/status"),
  startEnterpriseSso: (email: string) =>
    api.post<{ authorizationUrl: string }>("/auth/sso/start", { email }),
  pendingEnterpriseSsoMfa: () =>
    api.get<{ challengeToken: string }>("/auth/sso/pending-mfa"),
  login: async (email: string, password: string) => {
    const data = await api.post<AuthLoginResult>("/auth/login", { email, password });
    if (data.ok && data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  changeExpiredPassword: async (challengeToken: string, newPassword: string) => {
    const data = await api.post<AuthLoginSuccess | AuthMfaRequired>("/auth/password/change-expired", { challengeToken, newPassword });
    if (data.ok && data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  verifyMfa: async (challengeToken: string, code: string) => {
    const data = await api.post<AuthLoginSuccess>("/auth/mfa/challenge", { challengeToken, code });
    if (data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  mfaStatus: () => api.get<{ enabled: boolean; required: boolean; enabledAt: string | null; recoveryCodesRemaining: number }>("/auth/mfa/status"),
  setupMfa: () => api.post<{ secret: string; uri: string; qrDataUrl: string }>("/auth/mfa/setup", {}),
  enableMfa: (code: string) => api.post<{ ok: true; recoveryCodes: string[] }>("/auth/mfa/enable", { code }),
  disableMfa: (code: string) => api.post<{ ok: true }>("/auth/mfa/disable", { code }),
};

// FAX
export const faxApi = {
  sent: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<FaxTransmission>>(`/fax/sent?${qs}`);
  },
  received: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<FaxTransmission>>(`/fax/received?${qs}`);
  },
  get: (id: number) => api.get<FaxDetail>(`/fax/${id}`),
};

// Dashboard
export const dashboardApi = {
  stats: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return api.get<DashboardStats>(`/dashboard/stats?${params}`);
  },
};

export const orderConfirmationApi = {
  resolvePricing: (data: {
    source?: "endo" | "kikan";
    orderDate?: string;
    traderId?: number | null;
    traderCode?: string | null;
    senderFax?: string | null;
    senderTel?: string | null;
    items: Array<{
      itemId?: number | null;
      itemCode?: string | null;
      itemName?: string | null;
      quantity: number;
      unitPrice?: number | null;
      taxRate?: number | null;
    }>;
    charges?: Array<{
      code?: string | null;
      label: string;
      amount: number;
      taxRate?: number | null;
    }>;
    defaultTaxRate?: number | null;
    taxDisplayMode?: "none" | "total_only" | "by_rate";
    roundingMode?: "trunc" | "floor" | "round";
    useInputPriceFallback?: boolean;
  }) =>
    api.post<{ ok: boolean; data: {
      source: "endo" | "kikan";
      orderDate: string;
      taxDisplayMode: "none" | "total_only" | "by_rate";
      roundingMode: "trunc" | "floor" | "round";
      trader: { traderId: number | null; traderCode: string | null; catalogId: number | null };
      items: Array<{
        itemCode: string;
        itemName: string;
        quantity: number;
        unitPrice: number | null;
        amount: number;
        taxRate: number;
        taxAmount: number;
        priceSource: "catalog" | "master" | "input" | "missing";
        matchedItemId: number | null;
      }>;
      charges: Array<{
        code: string | null;
        label: string;
        amount: number;
        taxRate: number;
        taxAmount: number;
      }>;
      summary: {
        itemSubtotal: number;
        chargeSubtotal: number;
        subtotalExclTax: number;
        taxTotal: number;
        totalInclTax: number;
        taxByRate: Array<{ rate: number; taxable: number; tax: number }>;
      };
      warnings: string[];
    } }>("/order-confirmation/pricing/resolve", data),
  preview: (data: {
    confirmationNo: string;
    orderDate?: string | null;
    issuedAt?: string;
    companyName: string;
    companyNameKana?: string | null;
    companyAddress?: string | null;
    layoutMode?: "combined" | "by_warehouse";
    title?: string;
    note?: string | null;
    templateId?: string;
    config?: Record<string, unknown>;
    html?: string;
    charges?: Array<{
      code?: string | null;
      label: string;
      amount: number;
    }>;
    subtotalExclTax?: number | null;
    taxTotal?: number | null;
    totalAmount?: number | null;
    taxDisplayMode?: "none" | "total_only" | "by_rate";
    taxByRate?: Array<{ rate: number; taxable?: number; tax: number }>;
    items: Array<{
      itemCode?: string;
      itemName: string;
      quantity: number;
      unitPrice?: number | null;
      warehouseId?: number | null;
      warehouseName?: string | null;
      shippingDate?: string | null;
    }>;
  }) => api.postBlob("/order-confirmation/preview", data),
  templates: () =>
    api.get<{ data: Array<{
      id: string;
      name: string;
      originalFileName: string;
      templateType?: "coordinate" | "html" | "structured";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }> }>("/order-confirmation/templates"),
  importTemplate: (file: File, opts?: { name?: string; mapping?: Record<string, unknown> }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.name) fd.append("name", opts.name);
    if (opts?.mapping) fd.append("mapping", JSON.stringify(opts.mapping));
    return api.upload<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      templateType?: "coordinate" | "html" | "structured";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    } }>("/order-confirmation/templates/import", fd);
  },
  generateHtmlTemplate: (file: File, opts?: { name?: string; model?: string }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.name) fd.append("name", opts.name);
    if (opts?.model) fd.append("model", opts.model);
    return api.upload<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      templateType: "html";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }; htmlPreview: string }>("/order-confirmation/templates/generate-html", fd);
  },
  getTemplateHtml: (id: string) =>
    api.get<{ html: string }>(`/order-confirmation/templates/${id}/html`),
  updateTemplateHtml: (id: string, html: string) =>
    api.put<{ ok: boolean; template: {
      id: string;
      name: string;
      templateType: "html";
      createdAt: string;
      updatedAt: string;
    } }>(`/order-confirmation/templates/${id}/html`, { html }),
  generateStructuredTemplate: (file: File, opts?: { name?: string; model?: string }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.name) fd.append("name", opts.name);
    if (opts?.model) fd.append("model", opts.model);
    return api.upload<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      templateType: "structured";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }; config: Record<string, unknown> }>("/order-confirmation/templates/generate", fd);
  },
  getTemplateConfig: (id: string) =>
    api.get<{ config: Record<string, unknown> }>(`/order-confirmation/templates/${id}/config`),
  updateTemplateConfig: (id: string, config: Record<string, unknown>) =>
    api.put<{ ok: boolean; template: {
      id: string;
      name: string;
      templateType: "structured";
      createdAt: string;
      updatedAt: string;
    }; config: Record<string, unknown> }>(`/order-confirmation/templates/${id}/config`, { config }),
  updateTemplateMapping: (id: string, mapping: Record<string, unknown>) =>
    api.put<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    } }>(`/order-confirmation/templates/${id}/mapping`, { mapping }),
  removeTemplate: (id: string) =>
    api.delete<{ ok: boolean }>(`/order-confirmation/templates/${id}`),
  templatePdfUrl: (id: string) =>
    `/api/order-confirmation/templates/${encodeURIComponent(id)}/pdf`,
};

// Settings
export { settingsApi } from "./settings";
export type {
  OcrDocumentProfileRow,
  OcrDocumentProfileListResponse,
  OcrFormDefinitionInput,
  OcrFormDefinitionRow,
  OcrFormDefinitionListResponse,
} from "./settings";

// Send FAX
export const sendApi = {
  send: (phone: string, file: File, accountId?: number, provider?: "ictfax" | "sokufax_email", message?: string) => {
    const fd = new FormData();
    fd.append("phone", phone);
    fd.append("file", file);
    if (accountId) fd.append("account_id", String(accountId));
    if (provider) fd.append("provider", provider);
    if (message) fd.append("message", message);
    return api.upload<{
      ok: boolean;
      provider: "ictfax" | "sokufax_email";
      phone: string;
      transmissionId?: number;
      documentId?: number;
      queueItemId?: string;
      queued?: boolean;
      gatewayMessageId?: string | null;
    }>(
      "/send",
      fd,
    );
  },
  status: (txId: number) =>
    api.get<{ transmissionId: number; status: string; normalizedStatus?: string; statusDetail?: string | null; isStale?: boolean }>(`/send/status/${txId}`),
  queueStatus: (queueItemId: string) =>
    api.get<{
      queueItemId: string;
      transmissionId?: number | null;
      status: string;
      normalizedStatus?: string;
      statusDetail?: string | null;
      submittedAt?: string | null;
      completedAt?: string | null;
      createdAt?: string;
    }>(`/send/queue-status/${queueItemId}`),
  accounts: () => api.get<Array<{ account_id: number; phone: string; type: string }>>("/send/accounts"),
};

// Users
export const usersApi = {
  list: () =>
    api.get<
      Array<{
        id: string;
        email: string;
        name: string;
        picture: string | null;
        role: string;
        lastLogin: string | null;
        createdAt: string;
      }>
    >("/users"),
  updateRole: (id: string, role: string) =>
    api.put<{ ok: boolean }>(`/users/${id}/role`, { role }),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/users/${id}`),
};

// Dictionary
export const dictionaryApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<ProductAlias>>(`/dictionary?${qs}`);
  },
  create: (data: { type: string; alias: string; canonicalCode?: string; canonicalName: string }) =>
    api.post<{ ok: boolean; entry: ProductAlias; created?: boolean }>("/dictionary", data),
  update: (id: string, data: { alias?: string; canonicalCode?: string; canonicalName?: string }) =>
    api.put<{ ok: boolean; entry: ProductAlias }>(`/dictionary/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/dictionary/${id}`),
  import: (rows: Array<{ type: string; alias: string; canonicalCode?: string; canonicalName: string }>) =>
    api.post<{ ok: boolean; created: number; skipped: number; errors: Array<{ row: number; error: string }> }>(
      "/dictionary/import",
      { rows },
    ),
  suggestions: () =>
    api.get<{ data: DictionarySuggestion[] }>("/dictionary/suggestions"),
  suggestionsCount: () =>
    api.get<{ count: number }>("/dictionary/suggestions/count"),
  decideSuggestion: (data: {
    field: string;
    originalValue: string;
    correctedValue: string;
    status: DictionarySuggestionDecisionStatus;
    reason?: DictionarySuggestionRejectReason;
  }) =>
    api.post<{ ok: boolean }>("/dictionary/suggestions/decision", data),
};

// Product Mapping
export const productMappingApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<ProductMapping>>(`/product-mapping?${qs}`);
  },
  create: (data: { sourceCode: string; replacementCode: string; replacementName: string; notes?: string }) =>
    api.post<{ ok: boolean; entry: ProductMapping }>("/product-mapping", data),
  update: (id: string, data: { sourceCode?: string; replacementCode?: string; replacementName?: string; notes?: string; active?: boolean }) =>
    api.put<{ ok: boolean; entry: ProductMapping }>(`/product-mapping/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/product-mapping/${id}`),
  import: (rows: Array<{ sourceCode: string; replacementCode: string; replacementName: string; notes?: string }>) =>
    api.post<{ ok: boolean; created: number; skipped: number; errors: Array<{ row: number; error: string }> }>(
      "/product-mapping/import",
      { rows },
    ),
  apply: (id: string) => api.post<{ ok: boolean }>(`/product-mapping/${id}/apply`, {}),
};

// Import Code Maps
export const importCodeMapApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<ImportCodeMap>>(`/import-code-maps?${qs}`);
  },
  create: (data: {
    namespace: string;
    keyType: string;
    keyValue: string;
    canonicalCode?: string;
    canonicalName: string;
    payloadJson?: string | Record<string, unknown> | null;
    sourceType?: string;
    sourceName?: string;
    externalId?: string;
    active?: boolean;
  }) => api.post<{ ok: boolean; entry: ImportCodeMap }>("/import-code-maps", data),
  update: (id: string, data: Partial<{
    namespace: string;
    keyType: string;
    keyValue: string;
    canonicalCode: string;
    canonicalName: string;
    payloadJson: string | Record<string, unknown> | null;
    sourceType: string;
    sourceName: string;
    externalId: string;
    active: boolean;
  }>) => api.put<{ ok: boolean; entry: ImportCodeMap }>(`/import-code-maps/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/import-code-maps/${id}`),
  import: (
    rows: Array<{
      namespace?: string;
      keyType?: string;
      key_type?: string;
      keyValue?: string;
      key_value?: string;
      canonicalCode?: string;
      canonical_code?: string;
      canonicalName?: string;
      canonical_name?: string;
      payloadJson?: string | Record<string, unknown> | null;
      payload_json?: string | Record<string, unknown> | null;
      sourceType?: string;
      source_type?: string;
      sourceName?: string;
      source_name?: string;
      externalId?: string;
      external_id?: string;
      active?: boolean | string | number;
    }>,
    defaults?: { sourceType?: string; sourceName?: string },
  ) =>
    api.post<{ ok: boolean; upserted: number; skipped: number; errors: Array<{ row: number; error: string }> }>(
      "/import-code-maps/import",
      { rows, ...(defaults ?? {}) },
    ),
};

// Bulk Send
export const bulkApi = {
  send: (faxFile: File, csvFile: File) => {
    const fd = new FormData();
    fd.append("file", faxFile);
    fd.append("csv", csvFile);
    return api.upload<{
      ok: boolean;
      documentId: number;
      total: number;
      queued?: number;
      succeeded: number;
      failed: number;
      resultsTruncated?: boolean;
      resultSampleLimit?: number;
      results: Array<{ phone: string; queueItemId?: string; error?: string }>;
    }>("/bulk", fd);
  },
  history: () =>
    api.get<{ data: BulkRun[] }>("/bulk/history"),
  historyDetail: (id: string) =>
    api.get<BulkRunDetail>(`/bulk/history/${encodeURIComponent(id)}`),
  cancel: (id: string) =>
    api.post<{ ok: boolean; cancelled: number; draining: number }>(`/bulk/history/${encodeURIComponent(id)}/cancel`, {}),
};

export interface BulkRun {
  id: string;
  fileName: string | null;
  total: number;
  status: "running" | "paused" | "completed" | "failed" | "cancelled";
  succeeded: number;
  failed: number;
  cancelled: number;
  pending: number;
  queued: number;
  throughput: number;
  etaMinutes: number;
  createdByEmail: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface BulkRunDetail extends BulkRun {
  errors: Array<{ phone: string; error: string }>;
}

// Billing
export const billingApi = {
  usage: (yearMonth?: string) => {
    const path = yearMonth ? `/billing/usage/${yearMonth}` : "/billing/usage";
    return api.get<UsageSummary>(path);
  },
  plan: () => api.get<BillingPlan>("/billing/plan"),
  invoices: () => api.get<{ data: Invoice[] }>("/billing/invoices"),
  invoice: (id: string) => api.get<InvoiceDetail>(`/billing/invoices/${id}`),
  generateInvoice: (tenantId: string, year: number, month: number) =>
    api.post<{ ok: boolean; invoiceId: string; invoiceNumber: string }>(
      "/billing/invoices/generate",
      { tenantId, year, month },
    ),
  issueInvoice: (tenantId: string, year: number, month: number) =>
    api.post<{
      ok: boolean;
      invoiceId: string;
      invoiceNumber: string;
      status: string;
      total: number;
      notificationSent: boolean;
      alreadyNotified: boolean;
      zeroAmount: boolean;
      notificationStatus: string;
      notificationError: string | null;
      freeeSyncStatus: string;
      freeeDealId: string | null;
      freeeSyncError: string | null;
      pdfReady: boolean;
      pdfError: string | null;
    }>("/billing/invoices/issue", { tenantId, year, month }),
  updateInvoiceStatus: (id: string, status: string) =>
    api.put<{ ok: boolean; invoice: Invoice }>(`/billing/invoices/${id}/status`, { status }),
  addLineItem: (invoiceId: string, data: { description: string; unitPrice: number; quantity: number }) =>
    api.post<{ ok: boolean; invoice: InvoiceDetail }>(`/billing/invoices/${invoiceId}/line-items`, data),
  updateLineItem: (invoiceId: string, lineItemId: string, data: { description?: string; unitPrice?: number; quantity?: number }) =>
    api.put<{ ok: boolean; invoice: InvoiceDetail }>(`/billing/invoices/${invoiceId}/line-items/${lineItemId}`, data),
  deleteLineItem: (invoiceId: string, lineItemId: string) =>
    api.delete<{ ok: boolean; invoice: InvoiceDetail }>(`/billing/invoices/${invoiceId}/line-items/${lineItemId}`),
  points: () => api.get<PointBalance>("/billing/points"),
  pointHistory: (page?: number, limit?: number) => {
    const params = new URLSearchParams();
    if (page) params.set("page", String(page));
    if (limit) params.set("limit", String(limit));
    return api.get<{ data: PointTransaction[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/billing/points/history?${params}`);
  },
  createCheckout: (amount: number) =>
    api.post<{ url: string }>("/stripe/checkout", { amount }),
};

// API Keys (admin+)
export const apiKeysApi = {
  list: () => api.get<{ data: ApiKey[]; policy: ApiKeyManagementPolicy }>("/api-keys"),
  create: (data: { name: string; expiresAt?: string; scopes: ApiKeyScope[]; allowedSourceCidrs: string[]; requireIdempotency: boolean; replaceActiveTrialKey?: boolean }) =>
    api.post<ApiKeyCreateResponse>("/api-keys", data),
  updateSourceCidrs: (id: string, allowedSourceCidrs: string[]) =>
    api.patch<{ ok: true; allowedSourceCidrs: string[] }>(`/api-keys/${id}/source-cidrs`, { allowedSourceCidrs }),
  revoke: (id: string) => api.delete<{ ok: boolean }>(`/api-keys/${id}`),
};

// Platform Billing (platform_admin only)
export const platformBillingApi = {
  getBillingPlan: (tenantId: string) =>
    api.get<BillingPlan>(`/platform/tenants/${tenantId}/billing-plan`),
  summary: () => api.get<{ data: BillingSummaryItem[] }>("/platform/billing/summary"),
  generateAll: (year: number, month: number) =>
    api.post<{
      ok: boolean;
      total: number;
      succeeded: number;
      failed: number;
      results: Array<{ tenantId: string; tenantName: string; ok: boolean; invoiceNumber?: string; error?: string }>;
    }>("/platform/billing/generate-all", { year, month }),
  issueAll: (year: number, month: number) =>
    api.post<{
      ok: boolean;
      total: number;
      succeeded: number;
      failed: number;
      results: Array<{
        tenantId: string;
        tenantName: string;
        ok: boolean;
        invoiceNumber?: string;
        notificationStatus?: string;
        freeeSyncStatus?: string;
        error?: string;
      }>;
    }>("/platform/billing/issue-all", { year, month }),
  updateBillingPlan: (tenantId: string, data: Partial<BillingPlan>) =>
    api.put<{ ok: boolean; plan: BillingPlan }>(`/platform/tenants/${tenantId}/billing-plan`, data),
  getPoints: (tenantId: string) =>
    api.get<PointBalance>(`/platform/tenants/${tenantId}/points`),
  adjustPoints: (tenantId: string, amount: number, description: string) =>
    api.post<{ ok: boolean; balance: number; transaction: PointTransaction }>(`/platform/tenants/${tenantId}/points/adjust`, { amount, description }),
  getPointHistory: (tenantId: string, page?: number) => {
    const params = new URLSearchParams();
    if (page) params.set("page", String(page));
    return api.get<{ data: PointTransaction[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/platform/tenants/${tenantId}/points/history?${params}`);
  },
};

// Campaign (platform_admin only)
export const campaignApi = {
  segments: () =>
    api.get<{ data: Array<{ id: string; name: string; target: string; totalNumbers: number; recommendedTemplate: string; batchCount: number }> }>("/campaign/segments"),
  createSegment: (data: { name: string; target: string; recommendedTemplate: string; csv: File }) => {
    const fd = new FormData();
    fd.append("name", data.name);
    fd.append("target", data.target);
    fd.append("recommendedTemplate", data.recommendedTemplate);
    fd.append("csv", data.csv);
    return api.upload<{
      ok: boolean;
      segment: { id: string; name: string; target: string; totalNumbers: number; recommendedTemplate: string; batchCount: number };
      duplicatesRemoved: number;
    }>("/campaign/segments", fd);
  },
  deleteSegment: (id: string) =>
    api.delete<{ ok: boolean }>(`/campaign/segments/${encodeURIComponent(id)}`),
  templates: () =>
    api.get<{ data: Array<{ id: string; name: string; appeal: string; target: string; headline: string; previewUrl: string }> }>("/campaign/templates"),
  previewUrl: (id: string) => `/api/campaign/templates/${id}/preview`,
  send: (data: { segmentId: string; templateId: string; dryRun?: boolean; intervalMs?: number; confirmDuplicates?: boolean; skipDuplicates?: boolean }) =>
    api.post<{ ok: boolean; campaignId?: string; total?: number; totalBatches?: number; dryRun?: boolean; estimatedCost?: number; skippedDuplicates?: number; requiresConfirmation?: boolean; duplicates?: string[]; duplicateCount?: number; newCount?: number; totalCount?: number }>("/campaign/send", data),
  stop: (id: string) =>
    api.post<{ ok: boolean }>(`/campaign/${id}/stop`, {}),
  pause: (id: string) =>
    api.post<{ ok: boolean }>(`/campaign/${id}/pause`, {}),
  resume: (id: string) =>
    api.post<{ ok: boolean }>(`/campaign/${id}/resume`, {}),
  status: (id: string) =>
    api.get<{
      id: string; segmentId: string; templateId: string;
      total: number; queued: number; succeeded: number; failed: number; cancelled: number;
      retrying: number; throughput: number; etaMinutes: number;
      currentBatch: number; totalBatches: number;
      phase: "queuing" | "waiting" | "paused" | "done";
      status: "running" | "paused" | "completed" | "failed" | "cancelled";
      startedAt: string; completedAt?: string; dryRun: boolean;
      errors: Array<{ phone: string; error: string }>; totalErrors: number;
    }>(`/campaign/status/${id}`),
  list: () =>
    api.get<{ data: Array<{ id: string; segmentId: string; templateId: string; total: number; queued: number; succeeded: number; failed: number; cancelled: number; phase: string; status: string; startedAt: string; completedAt?: string; dryRun: boolean }> }>("/campaign/list"),
  transmissions: (id: string, opts?: { status?: string; sort?: string; order?: string; limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (opts?.status) p.set("status", opts.status);
    if (opts?.sort) p.set("sort", opts.sort);
    if (opts?.order) p.set("order", opts.order);
    if (opts?.limit) p.set("limit", String(opts.limit));
    if (opts?.offset) p.set("offset", String(opts.offset));
    const qs = p.toString() ? `?${p.toString()}` : "";
    return api.get<{
      data: Array<{ id: string; campaignId: string; phone: string; ictfaxTxId: number | null; status: string; error: string | null; createdAt: string; submittedAt: string | null; completedAt: string | null }>;
      summary: { total: number; queued: number; completed: number; failed: number };
      pagination?: { limit: number; offset: number; total: number };
    }>(`/campaign/${id}/transmissions${qs}`);
  },
};

// Chat
export const chatApi = {
  exportQuality: (params?: { since?: string; until?: string; limit?: number }) => {
    const search = new URLSearchParams();
    if (params?.since) search.set("since", params.since);
    if (params?.until) search.set("until", params.until);
    if (params?.limit) search.set("limit", String(params.limit));
    return api.getBlob(`/chat/conversations/quality-export?${search}`);
  },
  listConversations: (brand: "sphere" | "spfax", status?: string) => {
    const params = new URLSearchParams();
    params.set("brand", brand);
    if (status) params.set("status", status);
    return api.get<{ data: Array<{
      id: string; brand: string; visitorName: string | null; visitorEmail: string | null;
      supportState: string | null; claimedBy: string | null; claimedByEmail: string | null;
      claimedAt: string | null; firstResponseAt: string | null; slaDueAt: string | null;
      slaAlertedAt: string | null; handoffCount: number; loopDetectedAt: string | null;
      visitorCompany: string | null; visitorPhone: string | null;
      appUserId: string | null; appUserEmail: string | null; appUserName: string | null;
      appTenantId: string | null; appTenantName: string | null;
      lastSeenAt: string | null; lastLeftAt: string | null; visitorTypingAt: string | null;
      humanSeenAt: string | null; humanLeftAt: string | null; humanTypingAt: string | null;
      status: string; source: string; messageCount: number;
      maCid: string | null; firstReferrer: string | null; landingPage: string | null; currentUrl: string | null;
      utmSource: string | null; utmMedium: string | null; utmCampaign: string | null; utmTerm: string | null; utmContent: string | null;
      userAgent: string | null;
      lastMessage: { content: string; role: string; createdAt: string } | null;
      createdAt: string; updatedAt: string;
    }> }>(`/chat/conversations?${params}`);
  },
  getMessages: (brand: "sphere" | "spfax", conversationId: string) =>
    api.get<{
      messages: Array<{ id: string; role: string; content: string; createdAt: string }>;
      status: string;
      presence?: {
        visitorSeenAt: string | null;
        visitorLeftAt: string | null;
        visitorTypingAt: string | null;
        humanSeenAt: string | null;
        humanLeftAt: string | null;
        humanTypingAt: string | null;
      };
    }>(
      `/chat/admin/conversations/${conversationId}/messages?brand=${brand}`,
    ),
  presence: (brand: "sphere" | "spfax", conversationId: string, event: "seen" | "left" | "typing") =>
    api.post<{ ok: boolean; event: string }>(`/chat/conversations/${conversationId}/admin-presence?brand=${brand}`, { event }),
  reply: (brand: "sphere" | "spfax", conversationId: string, content: string) =>
    api.post<{ ok: boolean; message: { id: string; role: string; content: string; createdAt: string } }>(
      `/chat/conversations/${conversationId}/reply?brand=${brand}`,
      { content },
    ),
  claim: (brand: "sphere" | "spfax", conversationId: string) =>
    api.post<{ ok: boolean; claimedBy: string; claimedByEmail: string }>(
      `/chat/conversations/${conversationId}/claim?brand=${brand}`,
      {},
    ),
  release: (brand: "sphere" | "spfax", conversationId: string) =>
    api.post<{ ok: boolean }>(`/chat/conversations/${conversationId}/release?brand=${brand}`, {}),
  close: (brand: "sphere" | "spfax", conversationId: string) =>
    api.post<{ ok: boolean }>(`/chat/conversations/${conversationId}/close?brand=${brand}`, {}),
  listQa: (source?: string) => {
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    return api.get<{ data: Array<{
      id: string; question: string; answer: string; category: string | null;
      source: string; usageCount: number; showOnLp: boolean; lpPages: string; sortOrder: number;
      createdAt: string; updatedAt: string;
    }> }>(`/chat/qa?${params}`);
  },
  listQaSuggestions: (status?: "pending" | "approved" | "rejected" | "all") => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    return api.get<{ data: Array<{
      id: string;
      conversationId: string | null;
      question: string;
      answer: string;
      category: string | null;
      source: string;
      status: "pending" | "approved" | "rejected";
      origin: string | null;
      confidence: number | null;
      grounding: string | null;
      gateResult: string | null;
      gateReason: string | null;
      riskCategory: string | null;
      citations: string | null;
      numericClaims: string | null;
      reviewedBy: string | null;
      reviewedAt: string | null;
      createdAt: string;
      updatedAt: string;
    }> }>(`/chat/qa/suggestions?${params}`);
  },
  approveQaSuggestion: (id: string, data?: {
    question?: string;
    answer?: string;
    category?: string;
    showOnLp?: boolean;
    lpPages?: string;
    sortOrder?: number;
    confirmHighRisk?: boolean;
  }) => api.post<{ ok: boolean; entry: unknown; suggestion: unknown }>(`/chat/qa/suggestions/${id}/approve`, data ?? {}),
  rejectQaSuggestion: (id: string) =>
    api.post<{ ok: boolean; suggestion: unknown }>(`/chat/qa/suggestions/${id}/reject`, {}),
  createQa: (data: { question: string; answer: string; category?: string; showOnLp?: boolean; lpPages?: string; sortOrder?: number }) =>
    api.post<{ ok: boolean; entry: unknown }>("/chat/qa", data),
  updateQa: (id: string, data: { question?: string; answer?: string; category?: string; showOnLp?: boolean; lpPages?: string; sortOrder?: number }) =>
    api.put<{ ok: boolean; entry: unknown }>(`/chat/qa/${id}`, data),
  deleteQa: (id: string) =>
    api.delete<{ ok: boolean }>(`/chat/qa/${id}`),
};

// Self-Company Identification
export const selfCompanyApi = {
  get: () => api.get<{ names: string[]; faxNumbers: string[] }>("/settings/self-company"),
  update: (data: { names: string[]; faxNumbers: string[] }) =>
    api.put<{ ok: boolean; names: string[]; faxNumbers: string[] }>("/settings/self-company", data),
};

// Received-FAX → multi-recipient email forwarding
export const faxReceiveForwardApi = {
  get: () => api.get<{ emails: string[]; mode: "attachment" | "notification_only"; lockedBySecurityProfile: boolean }>("/settings/fax-receive-forward"),
  update: (emails: string[], mode: "attachment" | "notification_only") =>
    api.put<{ ok: boolean; emails: string[]; mode: "attachment" | "notification_only" }>("/settings/fax-receive-forward", { emails, mode }),
};

export interface SecurityProfileSettings {
  securityProfile: "standard" | "confidential" | "medical";
  mfaRequired: boolean;
  faxReceiveForwardMode: "attachment" | "notification_only";
  ocrDataClass: "standard" | "confidential" | "medical";
  ocrAllowGlobalProcessing: boolean;
  ocrRetentionDays: number | null;
  outboundFaxRetentionDays: number | null;
  outboundFaxRetentionOperational: boolean;
  userCount: number;
  missingMfaUsers: number;
}

export const securityProfileApi = {
  get: () => api.get<SecurityProfileSettings>("/settings/security-profile"),
  update: (profile: SecurityProfileSettings["securityProfile"], retentionDays?: number) =>
    api.put<{ ok: true } & Omit<SecurityProfileSettings, "userCount" | "missingMfaUsers">>("/settings/security-profile", {
      profile,
      ...(retentionDays === undefined ? {} : { retentionDays }),
    }),
};

// Email Intake
export interface SmtpIntakeCredential {
  id: string;
  username: string;
  active: boolean;
  allowedRcptAddress: string;
  allowedSourceCidrs: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface EmailIntakeSettingsResponse {
  emailToken: string | null;
  intakeAddress: string | null;
  domain: string;
  smtpIntake?: {
    host: string;
    port: number;
    credentials: SmtpIntakeCredential[];
  };
}

export interface SmtpIntakeCredentialCreateResponse {
  id: string;
  username: string;
  password: string;
  allowedRcptAddress: string;
  host: string;
  port: number;
}

export const emailIntakeApi = {
  get: () => api.get<EmailIntakeSettingsResponse>("/settings/email-intake"),
  generate: (reset?: boolean) =>
    api.post<{ emailToken: string; intakeAddress: string; domain: string; created: boolean }>("/settings/email-intake/generate", { reset }),
  createSmtpCredential: () =>
    api.post<SmtpIntakeCredentialCreateResponse>("/settings/email-intake/smtp-credentials", {}),
  deleteSmtpCredential: (id: string) =>
    api.delete<{ ok: boolean }>(`/settings/email-intake/smtp-credentials/${id}`),
};

// Platform (platform_admin only)
export type SupportAccessSession = {
  id: string;
  tenantId: string;
  tenantName: string;
  actorEmail: string;
  reason: string;
  ticketReference: string | null;
  accessLevel: "read_only" | "ocr_reprocess" | "dm_reply_classify";
  startedAt: string;
  expiresAt: string;
};

export type SupportDmReplyPageResponse = {
  page_number: number;
  search_code?: string;
  selected_actions: string[];
  selection_confidence: "high" | "medium" | "low";
  qr_url?: string;
};

export type SupportDmReplyCandidateResponse = {
  tenant: { id: string; name: string };
  ocrResultId: string;
  ocrVersion: string;
  reviewReason:
    | "no_search_code"
    | "tracking_link_not_found"
    | "multiple_search_codes"
    | "no_selected_action"
    | "conflicting_actions"
    | "respondent_needs_review"
    | "selected_action";
  analysis: {
    searchCodes: string[];
    actions: string[];
    pageResponses?: SupportDmReplyPageResponse[];
  };
  candidates: Array<{
    trackingLinkId: string;
    searchCode: string;
    campaignId: string;
    campaignName: string;
    advertiserName: string | null;
    recipientId: string;
    recipientCompany: string | null;
    recipientContactName: string | null;
    recipientFaxNumberMasked: string;
  }>;
};

export type SupportDmReplyConfirmPayload = {
  tenantId: string;
  outcome: "positive" | "unsubscribe" | "normal";
  campaignId: string;
  recipientId: string;
  searchCode: string;
  ocrVersion: string;
  note: string;
  selectedActions: string[];
  acknowledgeConflict: boolean;
  acknowledgeTarget: true;
};

export type SupportDmReplyConfirmResponse = {
  ok: true;
  operationId: string;
  outcome: "positive" | "unsubscribe" | "normal";
  faxType: string;
  idempotent: boolean;
};

export const platformApi = {
  listTenants: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<{ data: PlatformTenant[] }>(`/platform/tenants?${qs}`);
  },
  getRuntimeControls: () =>
    api.get<{ runtimeControls: RuntimeControls; uploadOcr: UploadOcrStatus; faxQueue: FaxSchedulerHealth | null }>("/platform/runtime-controls"),
  updateRuntimeControls: (data: {
    signupEnabled?: boolean;
    ocrGlobalPause?: boolean;
    dmReplyPageSplitEnabled?: boolean;
  }) =>
    api.put<{ ok: boolean; runtimeControls: RuntimeControls }>("/platform/runtime-controls", data),
  recoverFaxQueue: (data: { olderThanSeconds?: number; dryRun?: boolean }) =>
    api.post<{
      ok: boolean;
      dryRun: boolean;
      olderThanSeconds: number;
      recoveredDispatchingNoTx: number;
      recoveredDispatchingWithTx: number;
      failedSubmittedWithoutTx: number;
      skippedSubmittedWithTx: number;
    }>("/platform/admin/fax-queue/recover", data),
  getTenant: (id: string) =>
    api.get<PlatformTenantDetail>(`/platform/tenants/${id}`),
  getCurrentSupportSession: () =>
    api.get<{ session: SupportAccessSession | null; code?: string }>("/platform/support-sessions/current"),
  startSupportSession: (data: {
    tenantId: string;
    reason: string;
    ticketReference?: string;
    accessLevel: "read_only" | "ocr_reprocess" | "dm_reply_classify";
    durationMinutes: number;
  }) =>
    api.post<{ session: SupportAccessSession }>("/platform/support-sessions", data),
  endSupportSession: () =>
    api.delete<{ ok: true }>("/platform/support-sessions/current"),
  listSupportSuppressions: (
    tenantId: string,
    params?: { active?: boolean; limit?: number; offset?: number },
  ) => {
    const query = new URLSearchParams({ tenantId });
    if (params?.active !== undefined) query.set("active", String(params.active));
    if (params?.limit != null) query.set("limit", String(params.limit));
    if (params?.offset != null) query.set("offset", String(params.offset));
    return api.get<DmSuppressionsResponse>(
      `/platform/support-sessions/current/suppressions?${query.toString()}`,
    );
  },
  getSupportDmReplyCandidate: (tenantId: string, ocrResultId: string) =>
    api.get<SupportDmReplyCandidateResponse>(
      `/platform/support-sessions/current/dm-replies/${encodeURIComponent(ocrResultId)}/candidate?tenantId=${encodeURIComponent(tenantId)}`,
    ),
  confirmSupportDmReply: (
    ocrResultId: string,
    data: SupportDmReplyConfirmPayload,
  ) =>
    api.post<SupportDmReplyConfirmResponse>(
      `/platform/support-sessions/current/dm-replies/${encodeURIComponent(ocrResultId)}/confirm`,
      data,
    ),
  createTenant: (data: {
    name: string;
    domain: string;
    didNumber: string;
    ictfaxUsername: string;
    ictfaxPassword: string;
  }) =>
    api.post<{
      tenant: PlatformTenant;
      provisioning: {
        ictfaxUserId: number;
        didId: number;
        stepsCompleted: string[];
      };
    }>("/platform/tenants", data),
  updateTenant: (
    id: string,
    data: {
      name?: string;
      costPerSecond?: number;
      active?: boolean;
      plan?: string;
      lifecycleStatus?: PlatformTenant["lifecycleStatus"];
      verificationStatus?: PlatformTenant["verificationStatus"];
      sendMode?: PlatformTenant["sendMode"];
      trialExpiresAt?: string | null;
      archivedAt?: string | null;
    },
  ) => api.put<{ ok: boolean; tenant: PlatformTenant }>(`/platform/tenants/${id}`, data),
  enableTenantSendDemo: (id: string) =>
    api.post<{
      ok: boolean;
      tenant: PlatformTenant;
      pointBalance: number;
      topUp: number;
      demo: { days: number; ocrTrialPoints: number };
    }>(`/platform/tenants/${id}/send-demo`, {}),
  addAllowedFaxRecipient: (
    tenantId: string,
    data: { phone: string; label?: string; verificationReason?: string },
  ) =>
    api.post<{ ok: boolean; entry: AllowedFaxRecipient }>(
      `/platform/tenants/${tenantId}/allowed-fax-recipients`,
      data,
    ),
  removeAllowedFaxRecipient: (tenantId: string, recipientId: string) =>
    api.delete<{ ok: boolean }>(
      `/platform/tenants/${tenantId}/allowed-fax-recipients/${recipientId}`,
    ),
  addDomain: (tenantId: string, domain: string) =>
    api.post<{ ok: boolean; mapping: { id: string; domain: string } }>(
      `/platform/tenants/${tenantId}/domains`,
      { domain },
    ),
  removeDomain: (tenantId: string, domainId: string) =>
    api.delete<{ ok: boolean }>(
      `/platform/tenants/${tenantId}/domains/${domainId}`,
    ),
  createUser: (
    tenantId: string,
    data: { email: string; name: string; password: string; role: string },
  ) =>
    api.post<{ ok: boolean; user: { id: string; email: string; name: string; role: string } }>(
      `/platform/tenants/${tenantId}/users`,
      data,
    ),
  resetPassword: (tenantId: string, userId: string, password: string) =>
    api.put<{ ok: boolean }>(
      `/platform/tenants/${tenantId}/users/${userId}/password`,
      { password },
    ),
  listApiKeys: (tenantId: string) =>
    api.get<{ data: ApiKey[] }>(`/platform/tenants/${tenantId}/api-keys`),
  createApiKey: (tenantId: string, name: string, expiresAt?: string) =>
    api.post<ApiKeyCreateResponse>(`/platform/tenants/${tenantId}/api-keys`, { name, expiresAt }),
  revokeApiKey: (tenantId: string, keyId: string) =>
    api.delete<{ ok: boolean }>(`/platform/tenants/${tenantId}/api-keys/${keyId}`),
  getOcrCosts: (tenantId: string) =>
    api.get<{
      data: Array<{
        id: string;
        transmissionId: number | null;
        source: string;
        senderNumber: string | null;
        senderEmail: string | null;
        originalFileName: string | null;
        faxType: string | null;
        processedAt: string;
        inputTokens: number | null;
        outputTokens: number | null;
        costUsd: number;
      }>;
      model: string;
      pricing: { input: number; output: number };
      totalCostUsd: number;
    }>(`/platform/tenants/${tenantId}/ocr-costs`),
  notifySubscribers: () =>
    api.get<{ data: Array<{ id: string; email: string; name: string | null; company: string | null; phone: string | null; desiredNumber: string | null; areaCode: string | null; status: string | null; note: string | null; source: string | null; interest: string | null; maCid: string | null; createdAt: string }> }>("/platform/notify-subscribers"),
  leads: (params?: { interest?: string; take?: number }) => {
    const qs = new URLSearchParams();
    if (params?.interest) qs.set("interest", params.interest);
    if (params?.take) qs.set("take", String(params.take));
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<{
      data: Array<{
        id: string;
        emailDomain: string | null;
        companyName: string | null;
        contact: { name: string | null; company: string | null; phone: string | null; email: string | null };
        maCid: string | null;
        firstReferrer: string | null;
        landingPage: string | null;
        landingUrl: string | null;
        utmSource: string | null;
        utmMedium: string | null;
        utmCampaign: string | null;
        firstSeenAt: string;
        lastSeenAt: string;
        events: Array<{
          id: string;
          eventType: string;
          interest: string | null;
          sourceTable: string | null;
          sourceId: string | null;
          page: string | null;
          source: string | null;
          errorCode: string | null;
          createdAt: string;
        }>;
      }>;
      summary: {
        eventCounts: Record<string, number>;
        interestCounts: Record<string, number>;
      };
    }>(`/platform/leads${suffix}`);
  },
  aiAttribution: (days = 30) =>
    api.get<AiAttributionStats>(`/platform/attribution/ai-sources?days=${encodeURIComponent(String(days))}`),
  listDemoTenants: () =>
    api.get<{ data: DemoTenantSummary[]; testFaxNumbers: string[] }>("/platform/demo-tenants"),
  createDemoTenant: (data: {
    companyName: string;
    customerName: string;
    email: string;
    domain?: string;
    trialDays: number;
  }) =>
    api.post<{
      ok: boolean;
      tenant: {
        id: string;
        name: string;
        trialExpiresAt: string | null;
        lifecycleStatus: string;
        verificationStatus: string;
        sendMode: string;
      };
      user: { id: string; email: string; name: string; role: string };
      temporaryPassword: string;
      loginUrl: string;
      testFaxNumbers: string[];
      ocrTrial: { points: number; perPageFee: number; estimatedPages: number };
      faxProvisioning: {
        status: "pending" | "provisioned" | "already_provisioned" | "failed";
        stepsCompleted: string[];
        failedStep?: string;
      };
    }>("/platform/demo-tenants", data),
  provisionDemoTenantFax: (tenantId: string) =>
    api.post<{
      ok: boolean;
      faxProvisioning: {
        status: "provisioned" | "already_provisioned";
        stepsCompleted: string[];
      };
      reloginRequired: boolean;
    }>(`/platform/demo-tenants/${encodeURIComponent(tenantId)}/provision-fax`, {}),
  listTrialApplications: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialApplicationItem[];
      summary: Record<string, number>;
      canOperateNumbers: boolean;
      trialPlatformEnabled: boolean;
    }>(`/platform/trial-applications?${qs.toString()}`);
  },
  getTrialApplicationSummary: () => api.get<{
    summary: Record<string, number>;
    trialPlatformEnabled: boolean;
  }>("/platform/trial-applications/summary"),
  createTrialApplication: (data: {
    tenantId: string;
    requestedUseCase: string;
    requestedFeatures: Array<"inbound" | "ocr" | "outbound">;
    needsExternalSend: boolean;
    plannedRecipient?: string;
  }) => api.post<{ ok: true; grant: { id: string; status: "pending_review" } }>(
    "/platform/trial-applications",
    data,
  ),
  approveTrialApplication: (
    id: string,
    data: {
      businessIdentityChecked: true;
      useCaseAllowed: true;
      temporaryNumberTermsAccepted: true;
      reviewReference: string;
      reviewNotes?: string;
    },
  ) => api.put<{ ok: true; status: "approved" }>(
    `/platform/trial-applications/${id}/approve`,
    data,
  ),
  rejectTrialApplication: (
    id: string,
    data: { reviewReference: string; reason: string },
  ) => api.put<{ ok: true; status: "rejected" }>(
    `/platform/trial-applications/${id}/reject`,
    data,
  ),
  reserveTrialApplicationNumber: (
    id: string,
    data: { inventoryId: string; reservationReference: string },
  ) => api.put<{ ok: true; status: "reserved"; faxNumber: string }>(
    `/platform/trial-applications/${id}/reserve-number`,
    data,
  ),
  activateTrialApplication: (
    id: string,
    data: {
      tenantFaxNumberId: string;
      physicalRouteReady: true;
      ictfaxTenantMatched: true;
      noPendingInbound: true;
      customerNoticeReady: true;
      provisioningReference: string;
    },
  ) => api.put<{
    ok: true;
    status: "active";
    startsAt: string;
    expiresAt: string;
    sendMode: "test_only" | "allowlist";
  }>(`/platform/trial-applications/${id}/activate`, data),
  extendTrialApplication: (
    id: string,
    data: {
      extensionDays: number;
      customerRequested: true;
      usageAndConversionReviewed: true;
      approvalReference: string;
      reason: string;
    },
  ) => api.put<{
    ok: true;
    status: "extended";
    previousExpiresAt: string;
    expiresAt: string;
    extensionCount: number;
  }>(`/platform/trial-applications/${id}/extend`, data),
  listIntakeRecords: (params?: {
    status?: string;
    tenantId?: string;
    receiver?: string;
    token?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.tenantId) qs.set("tenantId", params.tenantId);
    if (params?.receiver) qs.set("receiver", params.receiver);
    if (params?.token) qs.set("token", params.token);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: PlatformIntakeRecord[];
      summary: Record<string, number>;
    }>(`/platform/admin/intake-records?${qs.toString()}`);
  },
  listTrialFaxNumbers: (params?: {
    status?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialFaxNumberInventoryItem[];
      summary: Record<string, number>;
    }>(`/platform/admin/trial-fax-numbers?${qs.toString()}`);
  },
  confirmTrialFaxNumberSuspension: (
    inventoryId: string,
    data: {
      physicalRouteStopped: true;
      confirmationReference: string;
      note?: string;
    },
  ) =>
    api.put<{
      ok: true;
      idempotent: boolean;
      inventory: {
        id: string;
        status: "suspended";
        suspendedAt: string | null;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/confirm-suspension`,
      data,
    ),
  startTrialFaxNumberReturnCheck: (
    inventoryId: string,
    data: {
      noPendingInbound: true;
      confirmationReference: string;
      note?: string;
    },
  ) =>
    api.put<{
      ok: true;
      idempotent: boolean;
      inventory: {
        id: string;
        status: "return_check";
        returnCheckStartedAt: string | null;
        returnCheckUntil: string | null;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/start-return-check`,
      data,
    ),
  retryTrialFaxNumberReturnCheck: (
    inventoryId: string,
    data: {
      physicalRouteStillStopped: true;
      noPendingInbound: true;
      confirmationReference: string;
      investigationSummary: string;
    },
  ) =>
    api.put<{
      ok: true;
      inventory: {
        id: string;
        status: "return_check";
        returnCheckStartedAt: string;
        returnCheckUntil: string;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/retry-return-check`,
      data,
    ),
  retireTrialFaxNumber: (
    inventoryId: string,
    data: {
      providerNumberRetired: true;
      confirmationReference: string;
      investigationSummary: string;
    },
  ) =>
    api.put<{
      ok: true;
      idempotent: boolean;
      inventory: {
        id: string;
        status: "retired";
        retiredAt: string | null;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/retire`,
      data,
    ),
  listTrialNotifications: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialNotificationDeliveryItem[];
      summary: Record<string, number>;
    }>(`/platform/admin/trial-notifications?${qs.toString()}`);
  },
  resolveTrialNotificationSent: (
    deliveryId: string,
    data: {
      sesDeliveryConfirmed: true;
      confirmationReference: string;
      note?: string;
    },
  ) => api.put<{
    ok: true;
    idempotent: boolean;
    delivery: { id: string; status: "sent"; resolvedAt: string | null };
  }>(`/platform/admin/trial-notifications/${deliveryId}/resolve-sent`, data),
  retryTrialNotification: (
    deliveryId: string,
    data: {
      sesNonDeliveryConfirmed: true;
      confirmationReference: string;
      note?: string;
    },
  ) => api.put<{
    ok: true;
    delivery: { id: string; status: "pending"; resolvedAt: string };
  }>(`/platform/admin/trial-notifications/${deliveryId}/retry`, data),
  listTrialDataRetentions: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialDataRetentionItem[];
      summary: Record<string, number>;
    }>(`/platform/admin/trial-data-retentions?${qs.toString()}`);
  },
  confirmTrialDataRetentionInventory: (
    retentionId: string,
    data: {
      ictfaxReviewed: true;
      s3Reviewed: true;
      localStorageReviewed: true;
      confirmationReference: string;
      note?: string;
    },
  ) => api.put<{
    ok: true;
    idempotent: boolean;
    deletionPerformed: false;
    retention: {
      id: string;
      status: "ready_for_deletion";
      reviewedAt: string | null;
    };
  }>(`/platform/admin/trial-data-retentions/${retentionId}/confirm-inventory`, data),
  listTrialConversions: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialConversionItem[];
      summary: Record<string, number>;
      canOperate: boolean;
    }>(`/platform/admin/trial-conversions?${qs.toString()}`);
  },
  previewTrialConversionPreparation: (
    trialGrantId: string,
    data: {
      targetPlan: "ocr_only" | "fax_send" | "fax_full";
      targetSendMode: "disabled" | "allowlist" | "normal";
      billingMode: "invoice" | "prepaid";
      monthlyBaseFee: number;
      perFaxSendFee: number;
      perOcrPageFee: number;
      productionDid?: string | null;
      productionSenderAccountId?: number | null;
      productionTenantFaxNumberId?: string | null;
      productionApiKeyId?: string | null;
      contractReference: string;
      businessVerificationReference: string;
      billingReference: string;
      productionResourceReference: string;
      apiSecurityReference: string;
    },
  ) => api.post<{ ok: true } & TrialConversionPreparationPreview>(
    `/platform/admin/trial-conversions/${trialGrantId}/preview-prepare`,
    data,
  ),
  prepareTrialConversion: (
    trialGrantId: string,
    data: {
      contractConfirmed: true;
      businessVerificationConfirmed: true;
      billingConfirmed: true;
      productionResourcesConfirmed: true;
      apiSecurityConfirmed: true;
      temporaryNumberNonContinuationConfirmed: true;
      normalSendPolicyReviewed: true;
      previewToken: string;
      targetPlan: "ocr_only" | "fax_send" | "fax_full";
      targetSendMode: "disabled" | "allowlist" | "normal";
      billingMode: "invoice" | "prepaid";
      monthlyBaseFee: number;
      perFaxSendFee: number;
      perOcrPageFee: number;
      productionDid?: string | null;
      productionSenderAccountId?: number | null;
      productionTenantFaxNumberId?: string | null;
      productionApiKeyId?: string | null;
      contractReference: string;
      businessVerificationReference: string;
      billingReference: string;
      productionResourceReference: string;
      apiSecurityReference: string;
    },
  ) => api.post<{
    ok: true;
    conversion: {
      id: string;
      tenantId: string;
      status: "preparing";
      suspensionRequested: boolean;
    };
  }>(`/platform/admin/trial-conversions/${trialGrantId}/prepare`, data),
  checkTrialConversionReadiness: (conversionId: string) =>
    api.post<{
      ok: true;
      status: "preparing" | "ready";
      blockers: string[];
    }>(`/platform/admin/trial-conversions/${conversionId}/check-readiness`, {}),
  previewTrialConversionPreflight: (conversionId: string) =>
    api.get<{ ok: true } & TrialConversionPreflight>(
      `/platform/admin/trial-conversions/${conversionId}/preflight`,
    ),
  finalizeTrialConversion: (
    conversionId: string,
    data: {
      finalStateReviewed: true;
      noPendingProcessingConfirmed: true;
      customerNoticeReady: true;
      conversionReference: string;
    },
  ) => api.put<{
    ok: true;
    atomic: true;
    status: "converted";
    tenantId: string;
    trialGrantId: string;
    convertedAt: string;
  }>(`/platform/admin/trial-conversions/${conversionId}/finalize`, data),
};

// Changelog (platform_admin only)
export const changelogApi = {
  list: () => api.get<{ data: Array<{ id: string; title: string; description: string; page: string; publishedAt: string; createdAt: string; updatedAt: string }> }>("/changelog"),
  feed: (audience: string = "sidebar") =>
    api.get<{ data: Array<{ id: string; title: string; description: string; publishedAt: string }> }>(
      `/changelog/feed?audience=${encodeURIComponent(audience)}`,
    ),
  create: (data: { title: string; description: string; page: string; publishedAt?: string }) =>
    api.post<{ data: { id: string; title: string; description: string; page: string; publishedAt: string } }>("/changelog", data),
  update: (id: string, data: { title?: string; description?: string; page?: string; publishedAt?: string }) =>
    api.put<{ data: { id: string; title: string; description: string; page: string; publishedAt: string } }>(`/changelog/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/changelog/${id}`),
};

export type AnnouncementSeverity = "info" | "important" | "critical";
export type AnnouncementStatus = "draft" | "pending" | "published" | "archived";

export interface AnnouncementItem {
  id: string;
  source: "system" | "tenant";
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  pinned: boolean;
  publishedAt: string;
  expiresAt: string | null;
  contentVersion: number;
  dismissed: boolean;
}

export interface AnnouncementAdminItem {
  id: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  pinned: boolean;
  status: AnnouncementStatus;
  publishedAt: string | null;
  expiresAt: string | null;
  contentVersion: number;
  gateResult: string | null;
  gateReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AnnouncementCreateInput = {
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  pinned?: boolean;
  expiresAt?: string | null;
  status?: AnnouncementStatus;
};

export type AnnouncementUpdateInput = {
  title?: string;
  body?: string;
  severity?: AnnouncementSeverity;
  pinned?: boolean;
  expiresAt?: string | null;
};

export const announcementsApi = {
  listVisible: () => api.get<{ data: AnnouncementItem[] }>("/announcements"),
  dismiss: (id: string) => api.post<{ ok: boolean }>(`/announcements/${id}/dismiss`, {}),
  dismissTenant: (id: string) => api.post<{ ok: boolean }>(`/announcements/tenant/${id}/dismiss`, {}),
  adminList: () => api.get<{ data: AnnouncementAdminItem[] }>("/announcements/admin"),
  create: (data: AnnouncementCreateInput) =>
    api.post<{ data: AnnouncementAdminItem; queued?: number; queueError?: string }>("/announcements/admin", data),
  update: (id: string, data: AnnouncementUpdateInput) =>
    api.put<{ data: AnnouncementAdminItem }>(`/announcements/admin/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/announcements/admin/${id}`),
  publish: (id: string) =>
    api.post<{ data: AnnouncementAdminItem; queued?: number; queueError?: string }>(`/announcements/admin/${id}/publish`, {}),
};

// Onboarding Wizard
export interface CsvColumnDef {
  ocrField: string;
  headerLabel: string;
  defaultValue?: string;
  format?: string;
}

export interface CsvTemplateRow {
  id: string;
  tenantId: string;
  name: string;
  presetKey: string | null;
  encoding: string;
  columns: CsvColumnDef[];
  headerRow: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export const csvTemplateApi = {
  list: () => api.get<{ templates: CsvTemplateRow[] }>("/csv-templates"),
  presets: () => api.get<{ presets: Array<{ key: string; name: string; description: string }> }>("/csv-templates/presets"),
  create: (data: {
    name: string;
    presetKey?: string;
    encoding?: string;
    columns?: CsvColumnDef[];
    headerRow?: boolean;
    isDefault?: boolean;
  }) => api.post<CsvTemplateRow>("/csv-templates", data),
  update: (id: string, data: Partial<{
    name: string;
    encoding: string;
    columns: CsvColumnDef[];
    headerRow: boolean;
    isDefault: boolean;
  }>) => api.put<CsvTemplateRow>(`/csv-templates/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/csv-templates/${id}`),
  exportUrl: (templateId: string, params?: Record<string, string>) => {
    const p = { ...params };
    const qs = new URLSearchParams({ format: "csv", status: "approved", templateId, ...p }).toString();
    return `/api/ocr/export?${qs}`;
  },
};

// Custom Columns
export type CustomColumnDef = {
  id: string;
  key: string;
  label: string;
  source: "ocr" | "manual";
  dataType: "string" | "number" | "date" | "datetime" | "boolean";
  valueFormat: string;
  description: string;
  sortOrder: number;
};

export const columnsApi = {
  list: () => api.get<{ columns: CustomColumnDef[] }>("/settings/columns"),
  create: (data: {
    key: string;
    label: string;
    source: "ocr" | "manual";
    description?: string;
    dataType?: "string" | "number" | "date" | "datetime" | "boolean";
    valueFormat?: string;
  }) =>
    api.post<CustomColumnDef>("/settings/columns", data),
  update: (id: string, data: Partial<CustomColumnDef>) =>
    api.put<CustomColumnDef>(`/settings/columns/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/settings/columns/${id}`),
  reorder: (ids: string[]) => api.put<{ ok: boolean }>("/settings/columns/reorder", { ids }),
  restore: (id: string) => api.post<{ ok: boolean }>(`/settings/columns/${id}/restore`, {}),
};

export const onboardingApi = {
  status: () => api.get<OnboardingStatus>("/onboarding/status"),
  complete: (data: {
    industry: string;
    faxTypes: string[];
    primaryFaxType?: string;
    brandNames?: string[];
    selfFaxNumbers?: string[];
    itemIdentification?: string;
    handwritingLevel?: string;
    useConnector?: "yes" | "later" | "no";
    otherDetails?: Record<string, string>;
    marketingNotes?: string;
    monthlyVolume: string;
    currentService: string;
    desiredFields: string[];
    targetSystem: string;
    senderMatchMethod?: string;
    faxNumberOnPdf?: boolean;
    deliveryDateGranularity?: "order" | "item" | "both";
    contactFieldMode?: "order_only" | "shipping_only" | "both";
  }) => api.post<OnboardingCompleteResponse>("/onboarding/complete", data),
  skip: () => api.post<{ ok: boolean }>("/onboarding/skip", {}),
  resetAll: () => api.post<{ ok: boolean; ocrReverted: number; localStorageKeys: string[] }>("/onboarding/reset-all", {}),
  // 0AB-J 番ポ事前登録 (waitlist)。認定取得時に通知メールを受け取りたいユーザーが opt-in。
  getNumberPortingInterest: () =>
    api.get<{
      numberPortingInterest: boolean;
      numberPortingInterestAt: string | null;
      numberPortingNotifiedAt: string | null;
    }>("/onboarding/number-porting-interest"),
  setNumberPortingInterest: (interest: boolean) =>
    api.post<{
      ok: boolean;
      numberPortingInterest: boolean;
      numberPortingInterestAt: string | null;
    }>("/onboarding/number-porting-interest", { interest }),
};

// FAX Activation
export type FaxActivationPlan = {
  id: string;
  name: string;
  monthlyFee: number;
  perFaxSendFee: number;
  setupFee: number;
  features: string[];
};

export type FaxActivationStatus = {
  plan: string;
  didNumber: string | null;
  provisionedAt: string | null;
  activation: {
    id: string;
    plan: string;
    status: string;
    numberType: string | null;
    assignedDid: string | null;
    createdAt: string;
    autoProvisionJob: {
      id: string;
      status: string;
      attempt: number;
      maxAttempts: number;
      retryAfter: string | null;
      completedAt: string | null;
      lastError: string | null;
      lastErrorCode: string | null;
    } | null;
    openingApplication: {
      approvalStatus: string;
      registryDocumentStatus: string;
      postalVerifications: Array<{
        status: string;
        dispatchRequestStatus: string;
        dispatchedAt: string | null;
        expiresAt: string;
        attemptCount: number;
        maxAttempts: number;
      }>;
    } | null;
  } | null;
};

export type FaxOpeningApplicant = {
  corporateNumber: string;
  postalCode: string;
  registeredAddress: string;
  contactName: string;
  contactRole: string;
  contactPhone: string;
  usePurpose: string;
  monthlySendVolume: number;
  faxDmPurpose: boolean;
};

export type FaxOpeningAdminRecord = {
  id: string;
  tenantId: string;
  plan: string;
  status: string;
  numberType: string | null;
  portingNumber: string | null;
  portingCarrier: string | null;
  createdAt: string;
  tenant: { name: string; didNumber: string | null };
  autoProvisionJob: {
    id: string;
    status: string;
    attempt: number;
    maxAttempts: number;
    retryAfter: string | null;
    claimedAt: string | null;
    completedAt: string | null;
    lastError: string | null;
    lastErrorCode: string | null;
  } | null;
  openingApplication: (FaxOpeningApplicant & {
    id: string;
    serviceType: string;
    companyCheckStatus: string;
    corporateLookupStatus: string;
    corporateLookupCheckedAt: string | null;
    corporateOfficialName: string | null;
    corporateOfficialPostalCode: string | null;
    corporateOfficialAddress: string | null;
    corporateLookupError: string | null;
    riskReviewStatus: string;
    registryDocumentStatus: string;
    approvalStatus: string;
    reviewNotes: string | null;
    approvedAt: string | null;
    postalVerifications: Array<{
      id: string;
      status: string;
      dispatchRequestStatus: string;
      dispatchRequestError: string | null;
      dispatchedAt: string | null;
      trackingNumber: string | null;
      expiresAt: string;
      verifiedAt: string | null;
    }>;
  }) | null;
};

export const faxActivationApi = {
  plans: () => api.get<{ plans: FaxActivationPlan[] }>("/fax-activation/plans"),
  status: () => api.get<FaxActivationStatus>("/fax-activation/status"),
  apply: (data: FaxOpeningApplicant & { plan: string; numberType?: string; portingNumber?: string; portingCarrier?: string }) =>
    api.post<{ activation: { id: string; status: string }; message: string }>("/fax-activation/apply", data),
  upgrade: (data: FaxOpeningApplicant & { numberType: string; portingNumber?: string; portingCarrier?: string }) =>
    api.post<{ message: string }>("/fax-activation/upgrade", data),
  verifyPostalCode: (code: string) =>
    api.post<{ ok: boolean; verified: boolean; message: string }>("/fax-activation/verify-postal-code", { code }),
  downgrade: () => api.post<{ message: string }>("/fax-activation/downgrade", {}),
  cancel: () => api.post<{ message: string }>("/fax-activation/cancel", {}),
};

export const faxOpeningAdminApi = {
  list: (tenantId: string) =>
    api.get<{ data: FaxOpeningAdminRecord[] }>(
      `/fax-activation/admin/list?${new URLSearchParams({ tenantId })}`,
    ),
  startExistingOpening: (tenantId: string, data: FaxOpeningApplicant) =>
    api.post<{
      activation: { id: string; status: string };
      notifications: {
        customerEmail: "sent" | "failed";
        postalDispatch: "sent" | "failed";
        adminEmail: "sent" | "failed";
      };
      message: string;
    }>(`/fax-activation/admin/tenants/${tenantId}/start-existing-opening`, data),
  generatePostalPdf: (activationId: string) =>
    api.postBlob(`/fax-activation/admin/${activationId}/generate-postal-pdf`, {}),
  markDispatched: (activationId: string, trackingNumber: string) =>
    api.put<{ ok: true }>(`/fax-activation/admin/${activationId}/mark-dispatched`, { trackingNumber }),
  recheckCorporate: (activationId: string) =>
    api.post<{
      ok: true;
      status: string;
      checkedAt: string | null;
      officialName: string | null;
      officialPostalCode: string | null;
      officialAddress: string | null;
      error: string | null;
    }>(`/fax-activation/admin/${activationId}/recheck-corporate`, {}),
  approve: (
    activationId: string,
    data: {
      companyCheckStatus: "matched" | "manual_review" | "rejected";
      riskReviewStatus: "approved" | "manual_review" | "rejected";
      registryDocumentStatus: "not_required" | "received" | "verified" | "rejected";
      reviewNotes?: string;
    },
  ) => api.put<{ ok: true; approvedAt: string; autoProvisionQueued: boolean }>(
    `/fax-activation/admin/${activationId}/approve-opening`,
    data,
  ),
  retryAutoProvision: (activationId: string) =>
    api.post<{ ok: true }>(`/fax-activation/admin/${activationId}/retry-auto-provision`, {}),
};
